import {defaultTo, find, isNotNil, any} from 'ramda';
import {isTruthy} from 'ramda-adjunct';

import {
  useGetCommentsQuery,
  useGetDocumentContextCountQuery,
  useGetTireSetCountQuery,
} from '@dms/api/core';
import {useGetCustomerV2Query, useGetVehicleCustomerQuery} from '@dms/api/customer';
import {useGetAdditionalNoteQuery, useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetVehicleLocalActionNoteQuery} from '@dms/api/vehicle';
import {
  useGetCaseItemsTotalAmountQuery,
  useGetIntegrationCustomerQuery,
  useGetMetadaServiceCaseQuery,
  useGetPendingServiceItemsQuery,
  useGetPendingServiceOrderReturnQuery,
  useGetPostponeJobsQuery,
  useGetServiceCaseActionsQuery,
  useGetServiceCaseAdditionalInformationQuery,
  useGetServiceCaseOrderCountQuery,
  useGetServiceCaseOrderVariantsQuery,
  useGetServiceCaseOrdersQuery,
  useGetServiceCheckInQuery,
  useGetVehicleServiceCampaignQuery,
} from '@dms/api/workshop';

interface UseServiceCaseDetailDataProps {
  serviceCaseId: string;
}

export function useServiceCaseDetailData(props: UseServiceCaseDetailDataProps) {
  const {serviceCaseId} = props;

  const serviceCaseQuery = useGetMetadaServiceCaseQuery({serviceCaseId});
  const checkInQuery = useGetServiceCheckInQuery({serviceCaseId});

  const vehicleQuery = useGetVehicleQuery(
    {vehicleId: defaultTo('', serviceCaseQuery.data?.vehicleId)},
    {skip: !serviceCaseQuery.data?.vehicleId}
  );

  const commentsQuery = useGetCommentsQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      recordId: defaultTo('', serviceCaseQuery.data?.vehicleId),
    },
    {skip: !serviceCaseQuery.data?.vehicleId}
  );

  const additionalNoteQuery = useGetAdditionalNoteQuery(
    {vehicleId: defaultTo('', serviceCaseQuery.data?.vehicleId)},
    {skip: !serviceCaseQuery.data?.vehicleId}
  );

  const postponedJobsQuery = useGetPostponeJobsQuery({serviceCaseId});
  const orderVariantsQuery = useGetServiceCaseOrderVariantsQuery({serviceCaseId});

  const vehicleCustomersQuery = useGetVehicleCustomerQuery(
    {vehicleId: vehicleQuery.data?.id},
    {skip: !vehicleQuery.data}
  );

  const additionalInformationQuery = useGetServiceCaseAdditionalInformationQuery({serviceCaseId});

  const vehicleOperatorCustomerQuery = useGetCustomerV2Query(
    {
      customerId:
        vehicleCustomersQuery.data?.find(
          (vc) => vc?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR'
        )?.customerId ?? '',
    },
    {
      skip: !vehicleCustomersQuery.data?.find(
        (vc) => vc?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR'
      )?.customerId,
    }
  );

  const integrationCustomerQuery = useGetIntegrationCustomerQuery(
    {
      customerId:
        vehicleCustomersQuery.data?.find(
          (vc) => vc?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR'
        )?.customerId ?? '',
    },
    {
      skip: !vehicleCustomersQuery.data?.find(
        (vc) => vc?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR'
      )?.customerId,
    }
  );

  const vehicleServiceCampaignQuery = useGetVehicleServiceCampaignQuery(
    {vehicleId: defaultTo('', serviceCaseQuery.data?.vehicleId)},
    {skip: !serviceCaseQuery.data?.vehicleId}
  );

  const vehicleTiresQuery = useGetTireSetCountQuery(
    {vehicleId: defaultTo('', serviceCaseQuery.data?.vehicleId), states: ['INSTOCK']},
    {skip: !serviceCaseQuery.data?.vehicleId || serviceCaseQuery.isLoading}
  );

  const serviceCaseOrdersQuery = useGetServiceCaseOrdersQuery({serviceCaseId});
  const serviceCaseOrderCountQuery = useGetServiceCaseOrderCountQuery({serviceCaseId});
  const serviceCaseActionsQuery = useGetServiceCaseActionsQuery({serviceCaseId});
  const vehicleLocalActionNoteQuery = useGetVehicleLocalActionNoteQuery(
    {vehicleId: serviceCaseQuery.data?.vehicleId},
    {skip: !serviceCaseQuery.data?.vehicleId}
  );

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'service-case', contextId: serviceCaseId},
    {skip: !serviceCaseId}
  );

  const itemsTotalAmountQuery = useGetCaseItemsTotalAmountQuery(
    {serviceCaseId},
    {skip: !serviceCaseId}
  );

  const pendingServiceItemsQuery = useGetPendingServiceItemsQuery({serviceCaseId});
  const pendingServiceOrderReturnQuery = useGetPendingServiceOrderReturnQuery({serviceCaseId});

  const isLoading = any(isTruthy, [
    serviceCaseQuery.isLoading,
    checkInQuery.isLoading,
    vehicleQuery.isLoading,
    vehicleCustomersQuery.isLoading,
    vehicleOperatorCustomerQuery.isLoading,
    additionalInformationQuery.isLoading,
    integrationCustomerQuery.isLoading,
    serviceCaseOrdersQuery.isLoading,
  ]);

  const isError = any(isTruthy, [
    serviceCaseQuery.isError,
    checkInQuery.isError,
    vehicleQuery.isError,
    vehicleCustomersQuery.isError,
    vehicleOperatorCustomerQuery.isError,
    additionalInformationQuery.isError,
    serviceCaseOrdersQuery.isError,
  ]);

  const vehicleOperator = find(
    (vehicleCustomer) =>
      isNotNil(vehicleCustomer) &&
      vehicleCustomer.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR',
    vehicleCustomersQuery.data ?? []
  );

  const recallMessage = vehicleLocalActionNoteQuery.currentData?.local_action_note_text;

  const activeServiceCampaignsLength =
    vehicleServiceCampaignQuery.data?.serviceCampaign?.filter(
      (campaign) => campaign.kind === 'ACTIVE_CAMPAIGN'
    ).length ?? 0;

  return {
    data: {
      serviceCase: serviceCaseQuery.data,
      checkIn: checkInQuery.data,
      vehicle: vehicleQuery.data,
      comments: commentsQuery.data ?? [],
      additionalNote: additionalNoteQuery.data,
      postponedJobs: postponedJobsQuery.data,
      orderVariants: orderVariantsQuery.data,
      vehicleCustomers: vehicleCustomersQuery.data,
      vehicleOperator,
      vehicleOperatorCustomer: vehicleOperatorCustomerQuery.data,
      integrationCustomer: integrationCustomerQuery.data,
      integrationCustomerError: integrationCustomerQuery.error,
      additionalInformation: additionalInformationQuery.data,
      vehicleServiceCampaign: vehicleServiceCampaignQuery.data,
      vehicleTires: vehicleTiresQuery.data,
      serviceCaseOrders: serviceCaseOrdersQuery.data,
      serviceCaseOrderCount: serviceCaseOrderCountQuery.data,
      serviceCaseActions: serviceCaseActionsQuery.data,
      recallMessage,
      documentCount: documentCountQuery.data,
      itemsTotalAmount: itemsTotalAmountQuery.data,
      pendingServiceItems: pendingServiceItemsQuery.data,
      pendingServiceOrderReturn: pendingServiceOrderReturnQuery.data,
      activeServiceCampaignsLength,
    },
    isLoading,
    isError,
  };
}
